import { Box, Button, Typography } from '@mui/material'

/**
 * The two dead ends: a route that does not exist, and a render that threw.
 *
 * Both were a bare `<h1>` on the page background — unstyled, and in the error case offering
 * the operator nothing to do but reload by hand. On a console someone keeps open all day,
 * the error state is the one screen you cannot afford to leave unfinished.
 *
 * `themed={false}` renders with NO MUI components and NO theme lookups, only plain elements
 * and inline styles. The error boundary in App.jsx sits OUTSIDE ThemeModeProvider — that is
 * the whole point of a boundary — so anything it renders must survive the theme itself
 * having been what failed.
 */
const SHELL = {
  // Full width, because the app shell is now top-left aligned (see App.css): without this
  // the flex box shrinks to its content and `alignItems: center` has nothing to centre in.
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  minHeight: '60vh',
  padding: '24px',
  textAlign: 'center',
}

const Fallback = ({ title, detail, actionLabel, onAction, themed = true }) => {
  if (!themed) {
    return (
      <div style={{ ...SHELL, fontFamily: 'system-ui, -apple-system, sans-serif', color: '#454f5c' }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: '#0e131a' }}>{title}</h1>
        {detail && <p style={{ margin: 0, fontSize: '13px' }}>{detail}</p>}
        {actionLabel && (
          <button
            type='button'
            onClick={onAction}
            style={{
              marginTop: '8px',
              padding: '6px 14px',
              fontSize: '13px',
              fontFamily: 'inherit',
              color: '#1c5cab',
              background: 'transparent',
              border: '1px solid #c3cfd9',
              borderRadius: '3px',
              cursor: 'pointer',
            }}
          >
            {actionLabel}
          </button>
        )}
      </div>
    )
  }

  return (
    <Box sx={SHELL}>
      <Typography variant='h3' sx={{ color: 'ink.primary' }}>{title}</Typography>
      {detail && <Typography variant='body2' sx={{ color: 'ink.secondary' }}>{detail}</Typography>}
      {actionLabel && (
        <Button variant='outlined' onClick={onAction} sx={{ mt: 1 }}>{actionLabel}</Button>
      )}
    </Box>
  )
}

export default Fallback
